// src/components/KeyValuesDashboard/components/tabs/CountryTab.tsx

import { useState } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { Globe, MapPin } from 'lucide-react';

import type { ComparisonResponse, CountryData } from '@/types/analytics';
import {
    formatCurrency,
    formatNumber,
    getMetricTotal,
    COUNTRY_MAP,
} from '@/utils/keyvalues-helpers';

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

interface CountryTabProps {
  data: ComparisonResponse;
  mode: 'all' | 'compare';
}

export function CountryTab({ data }: CountryTabProps) {
  const [selectedCountry, setSelectedCountry] = useState<CountryData | null>(null);
  const [tooltipContent, setTooltipContent] = useState('');
  const [mapMetric, setMapMetric] = useState<'revenue' | 'impressions' | 'clicks'>('revenue');

  const countryData = data.country_breakdown || [];

  // Criar mapa de dados por país (nome do TopoJSON)
  const countryDataMap = new Map();
  countryData.forEach(country => {
    const topoJsonName = COUNTRY_MAP[country.country];
    if (topoJsonName) {
      countryDataMap.set(topoJsonName, country);
    }
  });

  // Calcular valores máximo e mínimo para normalização
  const metricValues = countryData.map(c => {
    const total = c.values.reduce((sum, v) => 
      sum + getMetricTotal(v.metrics, mapMetric), 0
    );
    return total;
  });
  const maxMetric = Math.max(...metricValues);
  const minMetric = Math.min(...metricValues.filter(v => v > 0));

  // Função para obter cor do país
  const getCountryColor = (geoName: string) => {
    const country = countryDataMap.get(geoName);
    if (!country) return '#f1f5f9'; // slate-100

    const value = country.values.reduce((sum:any, v:any) =>
      sum + getMetricTotal(v.metrics, mapMetric), 0
    );
    
    if (value === 0) return '#f1f5f9';

    // Normalizar entre 0 e 1
    const normalized = (value - minMetric) / (maxMetric - minMetric);

    // Gradiente de cores minimalista
    if (normalized > 0.8) return '#6366f1'; // indigo-500
    if (normalized > 0.6) return '#818cf8'; // indigo-400
    if (normalized > 0.4) return '#a5b4fc'; // indigo-300
    if (normalized > 0.2) return '#c7d2fe'; // indigo-200
    return '#e0e7ff'; // indigo-100
  };

  const formatMetricValue = (value: number) => {
    switch (mapMetric) {
      case 'revenue': return formatCurrency(value);
      case 'impressions': return formatNumber(value) + ' impressões';
      case 'clicks': return formatNumber(value) + ' cliques';
    }
  };

  return (
    <div className="space-y-6">
      
      {/* Controles do Mapa */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Globe className="w-5 h-5 text-indigo-600" />
            Análise Geográfica
          </h3>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Métrica:</span>
            <select
              value={mapMetric}
              onChange={(e) => setMapMetric(e.target.value as any)}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              <option value="revenue">Receita</option>
              <option value="impressions">Impressões</option>
              <option value="clicks">Cliques</option>
            </select>
          </div>
        </div>

        {/* Mapa */}
        <div className="relative bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              scale: 140,
              center: [0, 20]
            }}
            className="w-full h-[500px]"
          >
            <ZoomableGroup zoom={1} center={[0, 20]}>
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const geoName = geo.properties.name;
                    const country = countryDataMap.get(geoName);
                    const isSelected = selectedCountry && COUNTRY_MAP[selectedCountry.country] === geoName;

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={isSelected ? '#8b5cf6' : getCountryColor(geoName)}
                        stroke="#ffffff"
                        strokeWidth={0.5}
                        style={{
                          default: { outline: 'none' },
                          hover: {
                            fill: country ? '#7c3aed' : '#e2e8f0',
                            outline: 'none',
                            cursor: country ? 'pointer' : 'default',
                          },
                          pressed: { outline: 'none' },
                        }}
                        onMouseEnter={() => {
                          if (country) {
                            const value = country.values.reduce((sum:any, v:any) =>
                              sum + getMetricTotal(v.metrics, mapMetric), 0
                            );
                            setTooltipContent(`${country.country}: ${formatMetricValue(value)}`);
                          }
                        }}
                        onMouseLeave={() => {
                          setTooltipContent('');
                        }}
                        onClick={() => {
                          if (country) {
                            setSelectedCountry(country);
                          }
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>

          {/* Tooltip */}
          {tooltipContent && (
            <div className="absolute top-4 left-4 bg-white px-4 py-2 rounded-lg shadow-lg border border-slate-200 text-sm font-medium">
              {tooltipContent}
            </div>
          )}

          {/* Legenda */}
          <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-4 border border-slate-200">
            <p className="text-xs font-semibold text-slate-700 mb-3">
              {mapMetric === 'revenue' && 'Receita'}
              {mapMetric === 'impressions' && 'Impressões'}
              {mapMetric === 'clicks' && 'Cliques'}
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#e0e7ff] border border-slate-300"></div>
                <span className="text-xs text-slate-600">Baixo</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#a5b4fc] border border-slate-300"></div>
                <span className="text-xs text-slate-600">Médio</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#6366f1] border border-slate-300"></div>
                <span className="text-xs text-slate-600">Alto</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#8b5cf6] border border-slate-300"></div>
                <span className="text-xs text-slate-600">Selecionado</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* País Selecionado - Detalhes */}
      {selectedCountry && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-violet-600" />
              {selectedCountry.country}
            </h3>
            <button
              onClick={() => setSelectedCountry(null)}
              className="text-sm text-slate-600 hover:text-slate-900 underline"
            >
              Limpar seleção
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200">
              <p className="text-sm text-slate-600 mb-1">Receita Total</p>
              <p className="text-2xl font-bold text-indigo-900">
                {formatCurrency(selectedCountry.total_revenue)}
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-4 border border-violet-200">
              <p className="text-sm text-slate-600 mb-1">Impressões</p>
              <p className="text-2xl font-bold text-violet-900">
                {formatNumber(selectedCountry.values.reduce((sum, v) => 
                  sum + getMetricTotal(v.metrics, 'impressions'), 0
                ))}
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
              <p className="text-sm text-slate-600 mb-1">Cliques</p>
              <p className="text-2xl font-bold text-purple-900">
                {formatNumber(selectedCountry.values.reduce((sum, v) => 
                  sum + getMetricTotal(v.metrics, 'clicks'), 0
                ))}
              </p>
            </div>
          </div>

          {/* Tabela de valores por país */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                    Valor
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">
                    Receita
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">
                    Impressões
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">
                    Cliques
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {selectedCountry.values.map((value) => (
                  <tr key={value.custom_value} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {value.custom_value}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">
                      {formatCurrency(getMetricTotal(value.metrics, 'revenue'))}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">
                      {formatNumber(getMetricTotal(value.metrics, 'impressions'))}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">
                      {formatNumber(getMetricTotal(value.metrics, 'clicks'))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Lista de Todos os Países */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900">
            Ranking de Países por Receita
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  País
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Receita Total
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Nº de Valores
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Ação
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {countryData.map((country, index) => (
                <tr key={country.country} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600 font-semibold text-sm">
                      {index + 1}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-slate-900">
                    {country.country}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-slate-900">
                    {formatCurrency(country.total_revenue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-slate-600">
                    {country.values.length}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => setSelectedCountry(country)}
                      className="text-sm text-indigo-600 hover:text-indigo-900 font-medium"
                    >
                      Ver detalhes
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
